import React from "react";
import { copyText } from "@/lib/clipboard";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Trash2, Copy, Star, CopyPlus } from "lucide-react";
import { toast } from "sonner";
import { fetchAllPages } from "@/lib/serverExport";
import { http } from "@/lib/api";
import { useAuth, hasPerm } from "@/context/AuthContext";
import { useResourceList, useResourceMutations } from "@/hooks/useResource";
import { PageHeader } from "@/components/kit/PageHeader";
import { DataTablePro } from "@/components/kit/DataTablePro";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { fmtNumber, fmtMoney, flagEmoji } from "@/lib/format";
import { useOptions } from "@/hooks/useOptions";
import { userOptions } from "@/lib/users";
import { useTenantColumn } from "@/hooks/useTenantColumn";
import { useTenants } from "@/hooks/useTenants";
import { StreamDrawer } from "@/drawers/StreamDrawer";

function IconBtn({ onClick, title, danger, testId, children }) {
  return (
    <button type="button" title={title} data-testid={testId}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={`flex h-8 w-8 items-center justify-center rounded-md transition-colors ${danger ? "text-muted-foreground hover:bg-[#EF4444]/10 hover:text-[#EF4444]" : "text-muted-foreground hover:bg-white/[0.06] hover:text-foreground"}`}>
      {children}
    </button>
  );
}

function StreamStatus({ row }) {
  const manualOff = (row.status || "active") === "paused";
  const offerSt = row.offer_status || "active";
  const ok = !manualOff && offerSt === "active";
  const reason = manualOff ? "Disabled manually" : offerSt === "deleted" ? "Offer unavailable" : offerSt !== "active" ? "Offer disabled" : "";
  const cls = ok ? "bg-[#22C55E]/15 text-[#22C55E] border-[#22C55E]/30" : "bg-[#EAB308]/15 text-[#EAB308] border-[#EAB308]/30";
  return (
    <div className="inline-flex flex-col items-start gap-0.5">
      <span title={reason} className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${cls}`} data-testid={`stream-status-${row.id}`}>
        {ok ? "Active" : "Disabled"}
      </span>
      {!ok && !manualOff && (
        <span className="text-[10px] text-muted-foreground/70" data-testid={`stream-status-reason-${row.id}`}>
          {offerSt === "deleted" ? "Offer unavailable" : "Offer disabled"}
        </span>
      )}
    </div>
  );
}

export default function Streams() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [drawer, setDrawer] = React.useState({ open: false, item: null });
  const [bulkDomain, setBulkDomain] = React.useState("");
  const [delStream, setDelStream] = React.useState({ open: false, ids: [], name: "", clicks: 0, conversions: 0 });
  const { data: offersData } = useResourceList("offers", {});
  // ---- Server-side pagination: DataTablePro reports page/size/applied filters; we fetch one page.
  const [srv, setSrv] = React.useState({ page: 1, size: 10, q: "", filters: {}, custom: {}, sort: null, dir: "asc" });
  const handleServerChange = React.useCallback((s) => setSrv(s), []);
  const qs = React.useMemo(() => {
    const p = new URLSearchParams();
    p.set("limit", String(srv.size));
    p.set("offset", String((srv.page - 1) * srv.size));
    if (srv.q.trim()) p.set("search", srv.q.trim());
    const f = srv.filters || {};
    ["buyer_id", "offer_id", "advertiser_id", "offer_status", "tenant_id"].forEach((k) => {
      const v = f[k];
      if (Array.isArray(v) ? v.length : v && v !== "all") p.set(k, Array.isArray(v) ? v.join(",") : v);
    });
    if (srv.sort) { p.set("sort", srv.sort); p.set("order", srv.dir || "asc"); }
    return p.toString();
  }, [srv]);
  const { data, isLoading } = useQuery({
    queryKey: ["streams", "server", qs],
    queryFn: async () => (await http.get(`/streams?${qs}`)).data,
    placeholderData: (prev) => prev,
  });
  const { remove } = useResourceMutations("streams");
  const tcol = useTenantColumn("streams");
  const { data: tenantsData } = useTenants();
  const { data: domainsData } = useQuery({ queryKey: ["domains"], queryFn: async () => (await http.get("/domains")).data });
  const { data: platformPub } = useQuery({ queryKey: ["platform-public"], queryFn: async () => (await http.get("/platform-settings/public")).data });
  const canDelete = hasPerm(user, "streams.delete");
  const canEdit = hasPerm(user, "streams.edit") && user.role !== "buyer";
  const isBuyer = user.role === "buyer";
  const opts = useOptions();

  // Staff-only filter options (Offer / Advertiser) — full lists, independent of the current page.
  const offerFilterOpts = React.useMemo(() => (offersData?.items || [])
    .map((o) => ({ value: o.id, seq: Number(o.tenant_seq ?? 0), label: `#${o.tenant_seq} ${o.name}`, search: `#${o.tenant_seq} ${o.tenant_seq} ${o.name} ${o.geo || ""}` }))
    .sort((a, b) => b.seq - a.seq), [offersData]);
  const advFilterOpts = React.useMemo(() => (opts.advertisers || [])
    .map((a) => ({ value: a.id, seq: Number(a.tenant_seq ?? 0),
                   label: a.tenant_seq != null ? `#${a.tenant_seq} ${a.name}` : (a.name || "—"),
                   search: `#${a.tenant_seq ?? ""} ${a.tenant_seq ?? ""} ${a.name || ""}` }))
    .sort((a, b) => b.seq - a.seq), [opts.advertisers]);

  const platformDefault = platformPub?.tracking_domain || "track.affilix.io";
  // Bulk edit domains = Platform Primary Domain + the tenant's OWN active tracking domains
  // (/api/domains is tenant-scoped, so other tenants' domains never appear here).
  const primaryDomain = platformPub?.active_platform_domain
    || (platformPub?.platform_origin || "").replace(/^https?:\/\//, "")
    || platformDefault;
  const domainOptions = React.useMemo(() => {
    const own = (domainsData?.items || [])
      .filter((d) => (d.usages || []).includes("tracking") && d.status === "active")
      .map((d) => d.domain);
    return Array.from(new Set([primaryDomain, ...own].filter(Boolean)));
  }, [domainsData, primaryDomain]);

  const applyBulkDomain = async (ids, clear, setBusy) => {
    if (!bulkDomain) return toast.error("Select a tracking domain first");
    setBusy(true);
    try {
      const { data: res } = await http.post("/streams/bulk-tracking-domain", { ids, tracking_domain: bulkDomain });
      qc.invalidateQueries({ queryKey: ["streams"] });
      const msg = res.fallback > 0
        ? `${res.applied} → ${bulkDomain}, ${res.fallback} → platform default (${res.platform_default})`
        : `${res.applied} stream(s) set to ${bulkDomain}`;
      toast.success("Tracking domain updated", { description: msg });
      setBulkDomain("");
      clear();
    } catch (e) {
      toast.error("Bulk update failed", { description: e?.response?.data?.detail || "Try again." });
    } finally {
      setBusy(false);
    }
  };

  const applyBulkStatus = async (ids, status, clear, setBusy) => {
    setBusy(true);
    try {
      const { data: res } = await http.post("/streams/bulk-status", { ids, status });
      qc.invalidateQueries({ queryKey: ["streams"] });
      const skipped = res.skipped || [];
      if (status === "active" && skipped.length) {
        const detail = skipped.slice(0, 5).map((s) => `#${s.tenant_seq ?? "?"} ${s.name || s.id} — ${s.reason}`).join("; ");
        toast.warning(`${res.updated} stream${res.updated === 1 ? "" : "s"} activated. ${skipped.length} could not be activated`,
          { description: `${detail}${skipped.length > 5 ? "…" : ""}. Enable the offer(s) first.` });
      } else {
        toast.success(`${res.updated} stream${res.updated === 1 ? "" : "s"} ${status === "active" ? "activated" : "disabled"}`);
      }
      clear();
    } catch (e) {
      toast.error("Status change failed", { description: e?.response?.data?.detail || "Try again." });
    } finally {
      setBusy(false);
    }
  };

  const bulkClone = async (ids, clear, setBusy) => {
    setBusy(true);
    try {
      const { data: res } = await http.post("/streams/bulk-clone", { ids });
      qc.invalidateQueries({ queryKey: ["streams"] });
      toast.success(`Cloned ${res.cloned} stream${res.cloned === 1 ? "" : "s"}`, { description: "Copies created with new stream keys and \u201C COPY\u201D name suffix." });
      clear();
    } catch (e) {
      toast.error("Clone failed", { description: e?.response?.data?.detail || "Try again." });
    } finally {
      setBusy(false);
    }
  };

  // Any delete (single or bulk) first asks the backend how much data the streams REALLY have.
  // Data present → warning dialog; zero clicks & conversions → silent delete as before.
  const requestDelete = async (ids, name) => {
    try {
      const { data: u } = await http.post("/streams/usage", { ids });
      if ((u.clicks || 0) > 0 || (u.conversions || 0) > 0) {
        setDelStream({ open: true, ids, name, clicks: u.clicks || 0, conversions: u.conversions || 0 });
        return;
      }
    } catch {
      // Check failed → be safe and still warn before deleting.
      setDelStream({ open: true, ids, name, clicks: null, conversions: null });
      return;
    }
    ids.forEach((id) => remove.mutate(id));
  };

  const copy = (text, msg) => copyText(text, msg);
  const shortLink = (link) => (link || "").replace(/^https?:\/\//, "");

  const columns = [
    { key: "tenant_seq", label: "ID", sortable: true, mono: true, sortValue: (r) => Number(r.tenant_seq || 0),
      render: (r) => <span className="font-mono text-xs text-muted-foreground">#{r.tenant_seq}</span> },
    { key: "name", label: "Stream Name", sortable: true, render: (r) => <span className="font-medium text-foreground">{r.name}</span> },
    { key: "offer_name", label: "GEO Offer", render: (r) => <span className="text-sm text-muted-foreground">{r.offer_name || "—"}</span> },
    ...(!isBuyer ? [{ key: "buyer_name", label: "Buyer",
      exportValue: (r) => (r.buyer_seq != null ? `#${r.buyer_seq} ${r.buyer_name || ""}` : (r.buyer_name || "")),
      render: (r) => (
        <div className="min-w-0" data-testid={`stream-buyer-${r.id}`}>
          <div className="truncate text-sm text-foreground">{r.buyer_seq != null ? `#${r.buyer_seq} ` : ""}{r.buyer_name || "—"}</div>
          {r.buyer_email && <div className="truncate text-[11px] text-muted-foreground">{r.buyer_email}</div>}
        </div>
      ) }] : []),
    { key: "stream_key", label: "Stream Key", render: (r) => (
      <button type="button" onClick={(e) => { e.stopPropagation(); copy(r.stream_key, "Stream key copied"); }}
        data-testid={`stream-key-${r.id}`} title="Click to copy"
        className="inline-flex items-center gap-1.5 rounded-md bg-white/[0.04] px-2 py-1 font-mono text-xs text-[#A78BFA] transition-colors hover:bg-white/[0.08]">
        {r.stream_key || "—"}<Copy className="h-3 w-3 opacity-60" />
      </button>
    ) },
    { key: "landing", label: "Landing", render: (r) => (
      <span data-testid={`stream-landing-${r.id}`} title="Landing (edit in stream settings)"
        className="inline-flex items-center gap-1.5 rounded-md bg-white/[0.04] px-2 py-1 text-xs text-muted-foreground">
        {r.landing_is_default && <Star className="h-3 w-3 fill-[#EAB308] text-[#EAB308]" />}
        <span className="max-w-[140px] truncate">{r.landing_effective || "—"}</span>
      </span>
    ) },
    { key: "status", label: "Status", render: (r) => <StreamStatus row={r} /> },
    { key: "clicks", label: "Clicks", align: "right", mono: true, sortable: true, sortValue: (r) => Number(r.clicks || 0), render: (r) => fmtNumber(r.clicks) },
    { key: "payout", label: "Payout", align: "right", mono: true,
      exportValue: (r) => r.payment_model === "RevShare"
        ? (r.payout_percent != null ? `${r.payout_percent}%` : "")
        : r.payment_model === "Hybrid"
          ? `CPA ${r.payout ?? ""} / RS ${r.payout_percent ?? ""}%`
          : (r.payout ?? ""),
      render: (r) => {
        // Multi-GEO: compact two-line "up to / $X" so the PAYOUT column keeps its width;
        // hover shows the per-GEO breakdown.
        const wrap = (inner) => r.payout_varies && r.payout_breakdown ? (
          <span className="group relative inline-flex cursor-help flex-col items-end leading-tight" data-testid={`stream-payout-varies-${r.id}`}>
            <span className="text-[9px] font-sans font-normal uppercase tracking-wide text-muted-foreground">up to</span>
            <span className="border-b border-dotted border-white/20">{inner}</span>
            <span className="pointer-events-none absolute right-0 top-full z-50 mt-1 hidden min-w-[150px] flex-col gap-0.5 rounded-lg border border-white/[0.12] bg-[#141417] p-2 text-left shadow-xl group-hover:flex">
              {r.payout_breakdown.map((b) => (
                <span key={b.geo} className="flex items-center justify-between gap-3 text-[11px]">
                  <span className="text-muted-foreground">{b.geo === "*" ? "🌐 Other GEOs" : `${flagEmoji(b.geo)} ${b.geo}`}</span>
                  <span className="font-mono text-foreground">{b.percent ? `${Number(b.value) || 0}%` : fmtMoney(b.value)}</span>
                </span>
              ))}
            </span>
          </span>
        ) : inner;
        if (r.payment_model === "RevShare") return (
          <span data-testid={`stream-payout-${r.id}`} className="font-mono text-sm text-[#F59E0B]">
            {wrap(r.payout_percent != null ? `${Number(r.payout_percent) || 0}%` : "—")}
          </span>
        );
        if (r.payment_model === "Hybrid") return (
          <span data-testid={`stream-payout-${r.id}`} className="inline-flex flex-col items-end leading-tight">
            <span className="font-mono text-[11px] text-[#22C55E]">CPA {wrap(r.payout != null ? fmtMoney(r.payout) : "—")}</span>
            <span className="font-mono text-[11px] text-[#F59E0B]">RS {r.payout_percent != null ? `${Number(r.payout_percent) || 0}%` : "—"}</span>
          </span>
        );
        return (
          <span data-testid={`stream-payout-${r.id}`} className="font-mono text-sm text-[#22C55E]">
            {wrap(r.payout != null ? fmtMoney(r.payout) : "—")}
          </span>
        );
      } },
    { key: "tracking_link", label: "Tracking Link", render: (r) => (
      <div className="flex items-center gap-1.5">
        <code className="max-w-[220px] truncate font-mono text-xs text-muted-foreground" title={r.tracking_link}>{shortLink(r.tracking_link)}</code>
        <IconBtn onClick={() => copy(r.tracking_link, "Tracking link copied")} title="Copy tracking link" testId={`copy-link-${r.id}`}><Copy className="h-3.5 w-3.5" /></IconBtn>
      </div>
    ) },
    { key: "_actions", label: "Actions", align: "right", render: (r) => (
      <div className="flex items-center justify-end gap-1">
        <IconBtn onClick={() => setDrawer({ open: true, item: r })} title="Edit" testId={`edit-stream-${r.id}`}><Pencil className="h-4 w-4" /></IconBtn>
        {canDelete && <IconBtn onClick={() => requestDelete([r.id], r.name)} title="Delete" danger testId={`delete-stream-${r.id}`}><Trash2 className="h-4 w-4" /></IconBtn>}
      </div>
    ) },
  ];

  const filters = [
    ...(tcol.show ? [{ key: "tenant_id", label: "Tenant", getValue: (r) => r.tenant_id, options: (tenantsData?.items || []).map((t) => ({ value: t.internalId, label: t.name })) }] : []),
    ...(!isBuyer ? [
      { key: "buyer_id", label: "Buyer", multi: true, getValue: (r) => r.buyer_id, options: userOptions(opts.users) },
      { key: "offer_id", label: "Offer", multi: true, getValue: (r) => r.offer_id, options: offerFilterOpts },
      { key: "advertiser_id", label: "Advertiser", multi: true, getValue: (r) => r.advertiser_id, options: advFilterOpts },
    ] : []),
    { key: "offer_status", label: "Availability", getValue: (r) => (r.offer_status === "active" ? "active" : "disabled"),
      options: [{ value: "active", label: "Active" }, { value: "disabled", label: "Offer disabled" }] },
  ];

  return (
    <div className="space-y-5">
      <PageHeader title="Streams" subtitle="Each stream is a unique tracking link pointing to a GEO offer." />
      <DataTablePro testId="streams-table" columns={tcol.inject(columns, 2)} rows={data?.items || []} loading={isLoading && !data}
        serverMode serverTotal={data?.total} onServerChange={handleServerChange} fetchAllRows={() => fetchAllPages("/streams", qs)}
        filters={filters} searchKeys={["name", "stream_key", "offer_name"]} searchPlaceholder="Search by name, key or offer..." exportName="streams"
        onRowClick={(r) => setDrawer({ open: true, item: r })} emptyText="No streams yet."
        selectable={canEdit} bulkActions={canDelete ? ["delete"] : []} onBulkAction={(action, ids) => { if (action === "delete") return requestDelete(ids, null); }}
        bulkExtra={({ ids, clear, busy, setBusy }) => (
          <div className="flex items-center gap-1.5" data-testid="streams-bulk-domain">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" disabled={busy} data-testid="bulk-set-status-btn" className="h-8 gap-1.5 border-white/[0.12] text-xs">Set status ▾</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="border-white/[0.1] bg-[#1a1a1e]">
                <DropdownMenuItem data-testid="bulk-status-active" onClick={() => applyBulkStatus(ids, "active", clear, setBusy)} className="cursor-pointer text-xs focus:bg-white/[0.06]">Active</DropdownMenuItem>
                <DropdownMenuItem data-testid="bulk-status-disabled" onClick={() => applyBulkStatus(ids, "disabled", clear, setBusy)} className="cursor-pointer text-xs focus:bg-white/[0.06]">Disabled</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {hasPerm(user, "streams.create") && (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => bulkClone(ids, clear, setBusy)}
                data-testid="bulk-clone-btn" className="h-8 gap-1.5 border-white/[0.12] text-xs">
                <CopyPlus className="h-3.5 w-3.5" /> Clone selected
              </Button>
            )}
            <Select value={bulkDomain} onValueChange={setBulkDomain}>
              <SelectTrigger className="h-8 w-[200px] border-white/[0.12] bg-[#0A0A0A] text-xs" data-testid="bulk-domain-select">
                <SelectValue placeholder="Change tracking domain…" />
              </SelectTrigger>
              <SelectContent className="border-white/[0.1] bg-[#1a1a1e]">
                {domainOptions.map((d) => (
                  <SelectItem key={d} value={d} className="text-xs">{d}{d === platformDefault ? " (platform default)" : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" disabled={busy || !bulkDomain} onClick={() => applyBulkDomain(ids, clear, setBusy)}
              data-testid="bulk-domain-apply" className="h-8 gap-1.5 bg-white text-xs text-black hover:bg-white/90">Apply</Button>
          </div>
        )}
        toolbarRight={hasPerm(user, "streams.create") && (
          <Button onClick={() => setDrawer({ open: true, item: null })} data-testid="add-stream-btn" className="h-9 gap-1.5 bg-white text-black hover:bg-white/90"><span className="text-base leading-none">+</span> Add Stream</Button>
        )} />
      <StreamDrawer open={drawer.open} item={drawer.item} onOpenChange={(o) => setDrawer((d) => ({ ...d, open: o }))} />

      <AlertDialog open={delStream.open} onOpenChange={(o) => setDelStream((s) => ({ ...s, open: o }))}>
        <AlertDialogContent className="border-white/[0.1] bg-[#141417]" data-testid="stream-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{delStream.ids.length > 1 ? `Delete ${delStream.ids.length} Streams?` : "Delete Stream?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {delStream.ids.length > 1
                ? <>The selected streams have collected traffic data. Deleting them will permanently remove:</>
                : <>Deleting <span className="font-medium text-foreground">{delStream.name || "this stream"}</span> will permanently remove:</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {delStream.clicks != null && (
            <div className="flex items-center gap-4 rounded-lg border border-[#EF4444]/25 bg-[#EF4444]/[0.06] px-3 py-2 text-sm" data-testid="stream-delete-stats">
              <span className="text-foreground"><b className="tabular-nums">{fmtNumber(delStream.clicks)}</b> click{delStream.clicks === 1 ? "" : "s"}</span>
              <span className="text-foreground"><b className="tabular-nums">{fmtNumber(delStream.conversions)}</b> conversion{delStream.conversions === 1 ? "" : "s"}</span>
              <span className="text-xs text-muted-foreground">will be deleted forever</span>
            </div>
          )}
          <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground" data-testid="stream-delete-consequences">
            <li>the Stream{delStream.ids.length > 1 ? "s" : ""} and tracking link{delStream.ids.length > 1 ? "s" : ""};</li>
            <li>all statistics collected;</li>
            <li>all clicks;</li>
            <li>all conversions;</li>
            <li>all related logs;</li>
            <li>each Stream is automatically removed from every Multi Link that uses it — remaining streams get their weights redistributed automatically; if none remain, the Multi Link becomes Disabled.</li>
          </ul>
          <p className="text-xs text-muted-foreground">Buyer balances, payouts and reports will be recalculated automatically. <span className="font-medium text-[#EF4444]">This action is permanent — the data cannot be restored.</span></p>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="stream-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="stream-delete-confirm" onClick={() => { delStream.ids.forEach((id) => remove.mutate(id)); setDelStream((s) => ({ ...s, open: false, ids: [] })); }} className="bg-[#EF4444] text-white hover:bg-[#dc2626]">Delete permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
